import {
  Button,
  Checkbox,
  cn,
  RadioGroup,
  RadioGroupProps,
  RiFilter3Line,
  RiMap2Line,
  Stack,
  StackProps,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  HABITAT_INTACTNESS_KEY,
  LayerFactValue,
  MAP_LAYER_FACTS,
  MapRastersFact,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { FormControl, FormField, FormFieldProps, FormItem, FormLabel, withSuspenseBoundary } from '@/components';
import { FormRadioItem } from '@/components/widgets/o/Form/FormRadio';
import { Picker, PickerContent, PickerTrigger, usePicker } from '@/components/widgets/o/Form/Picker';
import { useYearsRangeForHabitatIntactness } from '@/hooks/useYearsRangeForHabitatIntactness';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { range } from '@/utils/range';

import {
  ControlsFormFieldValues,
  InteractiveMapFormFields,
  InteractiveMapLayer,
  useInteractiveMapControlsContext,
} from '../hooks/useControlsForm';

export const MapControls = (props: StackProps) => {
  const controlsForm = useInteractiveMapControlsContext();
  const layer = controlsForm.watch(InteractiveMapFormFields.IndicatorLayer);

  return (
    <Stack direction='row' spacing={2} data-testid='interactive-map-controls' {...props}>
      <YearPicker />
      <LayerPicker />
      {layer === InteractiveMapLayer.HabitatIntactness && <ThreatFilters />}
    </Stack>
  );
};

const YearPicker = () => {
  const { t } = useTranslation();

  const controlsForm = useInteractiveMapControlsContext();
  const yearsRange = useYearsRangeForHabitatIntactness();

  if (!yearsRange) {
    return null;
  }

  const years = range(yearsRange[0], yearsRange[1])
    .reverse()
    .map((n) => n.toString());

  const selectedYear = controlsForm.watch('year');

  return (
    <Picker>
      <PickerTrigger data-testid='year-picker-trigger'>
        {selectedYear ?? t('shared.projects.project.conservation.interactiveMap.labels.year')}
      </PickerTrigger>
      <PickerContent data-testid='year-picker-content'>
        <FormRadioGroup name={InteractiveMapFormFields.Year}>
          {years.map((item) => (
            <FormRadioItem key={item} value={item} label={item} />
          ))}
        </FormRadioGroup>
      </PickerContent>
    </Picker>
  );
};

const LayerPicker = () => {
  const { t } = useTranslation();

  const controlsForm = useInteractiveMapControlsContext();
  const layer = controlsForm.watch(InteractiveMapFormFields.IndicatorLayer);

  const handleOnValueChange = (newValue: string | boolean | null | undefined) => {
    if (typeof newValue !== 'string') {
      return;
    }

    controlsForm.setValue(InteractiveMapFormFields.ThreatFilters, []);
    controlsForm.setValue(InteractiveMapFormFields.IndicatorLayer, newValue as InteractiveMapLayer);
  };

  const hiRasterFacts = usePlotFacts<MapRastersFact>([MAP_LAYER_FACTS.habitat_intactness_raster_urls]);
  const rasterUrls = getFactFromFactArray<LayerFactValue>(
    hiRasterFacts,
    MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  )?.value;
  const hasHabitatIntactnessData = Object.keys(rasterUrls ?? {}).includes(HABITAT_INTACTNESS_KEY);

  return (
    <Picker>
      <PickerTrigger leftAdornment={<RiMap2Line />} data-testid='layer-picker-trigger'>
        {layer
          ? t(`shared.projects.project.conservation.interactiveMap.labels.${layer as InteractiveMapLayer}`)
          : t('shared.projects.project.conservation.interactiveMap.labels.indicatorLayers')}
      </PickerTrigger>

      <PickerContent data-testid='layer-picker-content'>
        <FormRadioGroup name={InteractiveMapFormFields.IndicatorLayer} onValueChange={handleOnValueChange}>
          <FormRadioItem
            value={InteractiveMapLayer.HabitatIntactness}
            label={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
            disabled={!hasHabitatIntactnessData}
          />
        </FormRadioGroup>
      </PickerContent>
    </Picker>
  );
};

const ThreatFilters = withSuspenseBoundary(() => {
  const { t } = useTranslation();

  const plotFacts = usePlotFacts<MapRastersFact>([MAP_LAYER_FACTS.habitat_intactness_raster_urls]);
  const rasterUrls = getFactFromFactArray<LayerFactValue>(
    plotFacts,
    MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  )?.value;

  const controlsForm = useInteractiveMapControlsContext();

  const threatFilters: ThreatType[] | null | undefined = controlsForm.watch(InteractiveMapFormFields.ThreatFilters);

  const [selected, setSelected] = useState<typeof threatFilters>([]);
  const [isOpen, setIsOpen] = useState(false);

  const availableThreats = (Object.keys(rasterUrls ?? {}).filter(
    (threat) => ![ThreatType.no_threat_area, HABITAT_INTACTNESS_KEY].includes(threat),
  ) ?? []) as ThreatType[];

  if (availableThreats.length === 0) {
    return null;
  }

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const resetForm = () => {
    setSelected(threatFilters);
  };

  const handleCancelClick = () => {
    resetForm();
    closeDropdown();
  };

  const handleSubmitClick = () => {
    controlsForm.setValue(InteractiveMapFormFields.ThreatFilters, selected);
    closeDropdown();
  };

  return (
    <Picker
      open={isOpen}
      onOpenChange={(open) => {
        resetForm();
        setIsOpen(open);
      }}
    >
      <PickerTrigger leftAdornment={<RiFilter3Line />} data-testid='threat-picker-trigger'>
        {threatFilters?.length
          ? t('shared.projects.project.conservation.interactiveMap.labels.filtersApplied', {
              count: threatFilters.length,
            })
          : t('shared.projects.project.conservation.interactiveMap.labels.filterByThreats')}
      </PickerTrigger>

      <PickerContent data-testid='threat-picker-content'>
        <Stack spacing={4}>
          <Stack spacing={2}>
            {availableThreats.map((threat) => {
              return (
                <Checkbox
                  key={threat}
                  id={threat}
                  checked={selected?.includes(threat)}
                  onCheckedChange={() => {
                    const set = new Set(selected);
                    if (set.has(threat)) {
                      set.delete(threat);
                    } else {
                      set.add(threat);
                    }
                    setSelected(Array.from(set));
                  }}
                >
                  <FormLabel htmlFor={threat}>{t(`global.threatTypes.${threat}`)}</FormLabel>
                </Checkbox>
              );
            })}
          </Stack>
          <Stack direction='row-reverse' spacing={2}>
            <Button variant='contained' size='small' onClick={handleSubmitClick}>
              {t('shared.projects.project.conservation.interactiveMap.labels.applyFilter')}
            </Button>
            <Button variant='text' size='small' onClick={handleCancelClick}>
              {t('global.ui.buttons.cancel')}
            </Button>
          </Stack>
        </Stack>
      </PickerContent>
    </Picker>
  );
}, null);

type FormRadioGroupProps = RadioGroupProps & Pick<FormFieldProps<ControlsFormFieldValues>, 'name'>;

export const FormRadioGroup = ({ name, className, onValueChange, ...delegated }: FormRadioGroupProps) => {
  const controlsForm = useInteractiveMapControlsContext();
  const { setOpen } = usePicker();

  return (
    <FormField
      name={name}
      control={controlsForm.control}
      render={({ field: { onChange, ...field } }) => (
        <FormItem>
          <FormControl>
            <RadioGroup
              className={cn('flex flex-col gap-2', className)}
              {...field}
              onValueChange={(...args) => {
                onValueChange?.(...args);
                onChange(...args);
                setOpen(false);
              }}
              data-testid={`radiogroup-${field.name}`}
              {...delegated}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
